import { ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCatalogPaginationFilters, useSearchData } from '@client/catalog/hooks';
import { SORTING_OPTIONS } from '@client/catalog/utils/facets';
import { useTranslate } from '@client/i18n/hooks';
import { changeViewType, selectCatalogSettings } from '@client/settings/redux/redux';
import { bindActionCreators } from '@reduxjs/toolkit';
import { SortingOptionType } from 'b2b-common/core/catalog/Catalog.types';
import { ResultsViewOptions } from './ResultsViewOptions';
import { SelectOptionType } from './ResultsViewOptions.types';
import { Props } from './ResultsViewOptionsContainer.types';

export const ResultsViewOptionsContainer = ({ countFilters, renderMobileFilters }: Props): ReactElement => {
  const t = useTranslate();
  const { data } = useSearchData();
  const { paginationFilters, onPageSizeChange, onSortingChange } = useCatalogPaginationFilters();
  const { viewType, pageSize } = useSelector(selectCatalogSettings);
  const onViewTypeChange = bindActionCreators(changeViewType, useDispatch());
  const unfrozenDataSortingOptions = [...data.sortingOptions];

  const labelsMap = {
    [SORTING_OPTIONS.nameasc]: t('Name ascending', {}, 'sorting option: nameasc'),
    [SORTING_OPTIONS.namedesc]: t('Name descending', {}, 'sorting option: namedesc'),
    [SORTING_OPTIONS.priceasc]: t('Price ascending', {}, 'sorting option: priceasc'),
    [SORTING_OPTIONS.pricedesc]: t('Price descending', {}, 'sorting option: pricedesc'),
    [SORTING_OPTIONS.stockdesc]: t('Stock descending', {}, 'sorting option: stockdesc'),
  };

  const compareSortingOptions = (
    firstOption: SortingOptionType,
    secondOption: SortingOptionType,
  ): number => {
    const sortingOptionsPriority = [
      SORTING_OPTIONS.priceasc,
      SORTING_OPTIONS.pricedesc,
      SORTING_OPTIONS.nameasc,
      SORTING_OPTIONS.namedesc,
      SORTING_OPTIONS.stockdesc,
    ];

    const firstOptionName = firstOption.name;
    const secondOptionName = secondOption.name;
    const haveFirstSortingPriority = sortingOptionsPriority.includes(firstOptionName);
    const haveSecondSortinPriority = sortingOptionsPriority.includes(secondOptionName);

    switch (true) {
      case haveFirstSortingPriority && haveSecondSortinPriority:
        return (
          sortingOptionsPriority.indexOf(firstOptionName)
            - sortingOptionsPriority.indexOf(secondOptionName)
        );

      case haveFirstSortingPriority:
        return -1;

      case haveSecondSortinPriority:
        return 1;

      default:
        return 0;
    }
  };

  const sortingOptions = [
    {
      label: t('Best results', {}, 'default sorting option'),
      value: 0,
    },
    ...unfrozenDataSortingOptions
      .sort(compareSortingOptions)
      .map(order => ({
        label: labelsMap[order.name.toLowerCase()],
        value: order.name,
      })),
  ];

  const pageSizeOptions: SelectOptionType[] = data.site ? data.site.listView.allowedProductsPerPage
    .map((option) => ({
      label: String(option.numberOfProducts),
      value: option.numberOfProducts,
    })) : [];

  return (
    <ResultsViewOptions
      countFilters={countFilters}
      pageSizeOptions={pageSizeOptions}
      sortingOptions={sortingOptions}
      renderMobileFilters={renderMobileFilters}
      viewType={viewType}
      pageSize={pageSize}
      sorting={paginationFilters.sorting}
      onViewTypeChange={onViewTypeChange}
      onPageSizeChange={onPageSizeChange}
      onSortingChange={onSortingChange}
    />
  );
};
